import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results"]
  static values = { url: { type: String, default: "/search" } }

  connect() {
    this.timeout = null
    this.selectedIndex = -1
    this.createResultsContainer()
    this.handleClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
    if (this.resultsContainer) {
      this.resultsContainer.remove()
    }
  }

  createResultsContainer() {
    this.resultsContainer = document.createElement("div")
    this.resultsContainer.className = "search-results absolute left-0 right-0 mt-2 rounded-xl border border-slate-700 overflow-hidden hidden z-50"
    this.resultsContainer.style.cssText = "background: #1B365D; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);"
    this.inputTarget.parentElement.appendChild(this.resultsContainer)
  }

  focus() {
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  query() {
    clearTimeout(this.timeout)
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.timeout = setTimeout(() => {
      this.fetchResults(query)
    }, 200)
  }

  async fetchResults(query) {
    try {
      const sep = this.urlValue.includes("?") ? "&" : "?"
      const response = await fetch(`${this.urlValue}${sep}q=${encodeURIComponent(query)}`)
      const results = await response.json()
      this.renderResults(results)
    } catch (error) {
      console.error("Search error:", error)
    }
  }

  renderResults(results) {
    if (results.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="px-4 py-6 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 mx-auto text-slate-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
          <p class="text-sm text-slate-400">Aucun resultat</p>
        </div>
      `
      this.showResults()
      return
    }

    const grouped = this.groupByType(results)
    let html = '<div class="max-h-80 overflow-y-auto">'

    for (const [type, items] of Object.entries(grouped)) {
      const typeLabel = items[0].type_label
      html += `
        <div class="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-800/50">
          ${typeLabel}
        </div>
      `
      items.forEach((item, index) => {
        html += `
          <a href="${item.url}"
             class="search-result flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors border-b border-slate-700/50 last:border-0"
             data-index="${index}"
             data-action="mouseenter->search#highlightResult">
            <div class="flex-shrink-0 h-9 w-9 rounded-lg bg-gold/20 flex items-center justify-center">
              ${this.getIcon(item.icon)}
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-white truncate">${this.escapeHtml(item.label)}</p>
              ${item.subtitle ? `<p class="text-xs text-slate-400 truncate">${this.escapeHtml(item.subtitle)}</p>` : ''}
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </a>
        `
      })
    }

    html += '</div>'
    html += `
      <div class="px-4 py-2 border-t border-slate-700 text-center">
        <span class="text-xs text-slate-500">Appuyez sur <kbd class="px-1.5 py-0.5 bg-slate-700 rounded text-slate-300">↵</kbd> pour selectionner</span>
      </div>
    `

    this.resultsContainer.innerHTML = html
    this.selectedIndex = -1
    this.showResults()
  }

  groupByType(results) {
    return results.reduce((acc, item) => {
      const type = item.type
      if (!acc[type]) acc[type] = []
      acc[type].push(item)
      return acc
    }, {})
  }

  getIcon(iconName) {
    const icons = {
      "building-office": `<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>`,
      "user": `<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>`,
      "currency-euro": `<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14.25 7.756a4.5 4.5 0 1 0 0 8.488M7.5 10.5h5.25m-5.25 3h5.25M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>`
    }
    return icons[iconName] || icons["user"]
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  showResults() {
    this.resultsContainer.classList.remove("hidden")
  }

  hideResults() {
    this.resultsContainer.classList.add("hidden")
    this.selectedIndex = -1
  }

  highlightResult(event) {
    const allResults = this.resultsContainer.querySelectorAll(".search-result")
    allResults.forEach(el => el.classList.remove("bg-white/10"))
    event.currentTarget.classList.add("bg-white/10")
  }

  handleKeydown(event) {
    const allResults = this.resultsContainer.querySelectorAll(".search-result")
    if (allResults.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, allResults.length - 1)
        this.updateSelection(allResults)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection(allResults)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && allResults[this.selectedIndex]) {
          window.location.href = allResults[this.selectedIndex].href
        }
        break
      case "Escape":
        this.hideResults()
        this.inputTarget.blur()
        break
    }
  }

  updateSelection(allResults) {
    allResults.forEach((el, i) => {
      el.classList.toggle("bg-white/10", i === this.selectedIndex)
    })
    if (allResults[this.selectedIndex]) {
      allResults[this.selectedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }
}
